import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState, type ComponentType, type SVGProps } from "react";
import { createPortal } from "react-dom";
import { StarLogo } from "./StarLogo";
import { ConfirmDialog } from "./ConfirmDialog";
import {
  Icon3dSpace,
  IconAlert,
  IconCard,
  IconExit,
  IconGear,
  IconList,
  IconMenu,
  IconPlaceholder,
  IconStar2,
  IconStar4,
  IconX,
} from "./icons";
import { useAuth } from "../AuthContext";
import { useAppState } from "../AppStateContext";
import { formatPlanDate, planLabel } from "../plan";
import { useI18n, type TranslationKey } from "../i18n";
import "./Sidebar.css";

type Item = {
  labelKey: TranslationKey;
  path: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
};

const ITEMS: Item[] = [
  { labelKey: "nav.overview", path: "/overview", icon: IconPlaceholder },
  { labelKey: "nav.findings", path: "/findings", icon: IconStar2 },
  { labelKey: "nav.hypoteses", path: "/hypoteses", icon: IconStar4 },
  { labelKey: "nav.attackGraph", path: "/graph", icon: Icon3dSpace },
  { labelKey: "nav.liveFeed", path: "/feed", icon: IconList },
  { labelKey: "nav.reports", path: "/reports", icon: IconAlert },
];

/** `data-tour` anchors on the nav items the product tour points at. */
const TOUR_ANCHORS: Record<string, string | undefined> = {
  "/overview": "sidebar-overview",
  "/findings": "sidebar-findings",
  "/graph": "sidebar-graph",
  "/reports": "sidebar-reports",
};

export function Sidebar({ collapsed }: { collapsed?: boolean }) {
  const location = useLocation();
  const nav = useNavigate();
  const { signOut } = useAuth();
  const { subscription } = useAppState();
  const { t, lang } = useI18n();
  const [confirmOut, setConfirmOut] = useState(false);
  // Mobile only: the top bar can't hold every nav item, so they live behind a
  // hamburger drawer (desktop keeps the full rail and never opens this).
  const [menuOpen, setMenuOpen] = useState(false);
  const settingsActive = location.pathname.startsWith("/settings");
  // The badge shows how long the subscription runs — the plan's own name ("1
  // Month") says nothing about when it ends. With no plan it invites the user
  // to pick one. Either way it links to Payments in Settings.
  const periodEnd = subscription?.subscription.current_period_end;
  const validUntil =
    planLabel(subscription) != null ? formatPlanDate(periodEnd, lang) : null;
  const badgeTitle = validUntil
    ? `${t("settings.subscriptionUntil")} ${validUntil}`
    : t("settings.noPlanShort");

  // While the drawer is open, freeze the page scroll and let Escape dismiss it.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  const onSignOut = async () => {
    await signOut();
    nav("/", { replace: true });
  };

  const subscriptionRow = (
    <NavLink
      to="/settings"
      className="sidebar__scans"
      title={badgeTitle}
      aria-label={badgeTitle}
    >
      {/* Both are always rendered; CSS shows the text on the expanded desktop
          rail and swaps to the icon wherever the pill shrinks (collapsed rail).
          Full wording stays in the tooltip. */}
      <IconCard className="sidebar__scans-icon" width={24} height={24} />
      <span className="sidebar__scans-text">
        {validUntil ? (
          <>
            <span className="sidebar__scans-label">{t("settings.subscriptionUntil")}</span>
            <span className="sidebar__scans-count">{validUntil}</span>
          </>
        ) : (
          <span className="sidebar__scans-count">{t("settings.noPlanShort")}</span>
        )}
      </span>
    </NavLink>
  );

  return (
    <>
      <aside className={`sidebar${collapsed ? " sidebar--collapsed" : ""}`}>
        <div className="sidebar__logo">
          <StarLogo />
        </div>

        <nav className="sidebar__nav">
          {ITEMS.map(({ labelKey, path, icon: Icon }) => {
            const label = t(labelKey);
            return (
              <NavLink
                key={path}
                to={path}
                end={path === "/overview"}
                className={({ isActive }) =>
                  `sidebar__item${isActive ? " sidebar__item--active" : ""}`
                }
                title={label}
                data-tour={TOUR_ANCHORS[path]}
              >
                <Icon width={24} height={24} />
                <span className="sidebar__label">{label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="sidebar__footer">
          {subscriptionRow}
          <div className="sidebar__actions">
            <button
              type="button"
              className="sidebar__action"
              aria-label={t("nav.signOut")}
              title={t("nav.signOut")}
              onClick={() => setConfirmOut(true)}
            >
              <IconExit width={24} height={24} />
            </button>
            <NavLink
              to="/settings"
              className={`sidebar__action${settingsActive ? " sidebar__action--active" : ""}`}
              aria-label={t("nav.settings")}
              title={t("nav.settings")}
            >
              <IconGear width={24} height={24} />
            </NavLink>
          </div>
        </div>

        {/* Mobile-only trigger for the drawer; hidden on the desktop rail. */}
        <button
          type="button"
          className="sidebar__hamburger"
          aria-label={t("nav.menu")}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(true)}
        >
          <IconMenu width={24} height={24} />
        </button>
      </aside>

      {menuOpen
        ? createPortal(
            <div className="drawer-backdrop" onClick={() => setMenuOpen(false)}>
              <div
                className="drawer"
                role="dialog"
                aria-modal="true"
                aria-label={t("nav.menu")}
                // Keep clicks inside from reaching the backdrop, and close the
                // drawer once a nav link is followed (covers re-tapping the
                // current route, which wouldn't change the path).
                onClick={(e) => {
                  e.stopPropagation();
                  if ((e.target as HTMLElement).closest("a")) setMenuOpen(false);
                }}
              >
                <div className="drawer__head">
                  <StarLogo />
                  <button
                    type="button"
                    className="drawer__close"
                    aria-label={t("common.close")}
                    onClick={() => setMenuOpen(false)}
                  >
                    <IconX width={22} height={22} />
                  </button>
                </div>

                <nav className="drawer__nav">
                  {ITEMS.map(({ labelKey, path, icon: Icon }) => (
                    <NavLink
                      key={path}
                      to={path}
                      end={path === "/overview"}
                      className={({ isActive }) =>
                        `drawer__item${isActive ? " drawer__item--active" : ""}`
                      }
                    >
                      <Icon width={22} height={22} />
                      <span>{t(labelKey)}</span>
                    </NavLink>
                  ))}
                </nav>

                <div className="drawer__footer">
                  <NavLink to="/settings" className="drawer__item">
                    <IconCard width={22} height={22} />
                    <span>
                      {validUntil
                        ? `${t("settings.subscriptionUntil")} ${validUntil}`
                        : t("settings.noPlanShort")}
                    </span>
                  </NavLink>
                  <NavLink
                    to="/settings"
                    className={`drawer__item${settingsActive ? " drawer__item--active" : ""}`}
                  >
                    <IconGear width={22} height={22} />
                    <span>{t("nav.settings")}</span>
                  </NavLink>
                  <button
                    type="button"
                    className="drawer__item drawer__item--button"
                    onClick={() => {
                      setMenuOpen(false);
                      setConfirmOut(true);
                    }}
                  >
                    <IconExit width={22} height={22} />
                    <span>{t("nav.signOut")}</span>
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}

      <ConfirmDialog
        open={confirmOut}
        title={t("nav.confirmSignOutTitle")}
        body={t("nav.confirmSignOutBody")}
        confirmLabel={t("nav.signOut")}
        cancelLabel={t("common.cancel")}
        onConfirm={() => {
          setConfirmOut(false);
          void onSignOut();
        }}
        onClose={() => setConfirmOut(false)}
      />
    </>
  );
}
